import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Table, TableColumn, TableVariant } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading, Section } from '~/components';
import { RelativeTime } from '~/elements';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { Card, CardRepository } from '~/repositories/CardRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

import { CardAccountProps } from './CardAccount';

const columns: TableColumn<Card>[] = [
  {
    header: 'Authorized User',
    accessor: 'authorizedUserEntityId',
    width: { min: 140, max: '2fr' },
    cell: ({ value }) => <Table.TextCell weight="medium" value={value || '-'} />,
  },
  {
    header: 'BIN',
    accessor: 'bin',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={value || '-'} />,
  },
  {
    header: 'Last 4',
    accessor: 'lastFourDigits',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={value || '-'} />,
  },
  {
    header: 'Expires',
    accessor: 'expiresAt',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.TextCell value={value || '-'} />,
  },
  {
    header: 'Status',
    accessor: 'status',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value) : '-'} />,
  },
  {
    header: 'Type',
    accessor: 'type',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.ChipCell value={value ? formatString(value) : '-'} />,
  },
  {
    header: 'Created',
    accessor: 'createdAt',
    width: { min: 'min-content', max: '1fr' },
    cell: ({ value }) => <Table.Cell>{value ? <RelativeTime timestamp={value as Date} /> : '-'}</Table.Cell>,
  },
];

export const PageCardAccountCards: React.FC = () => {
  const navigate = useNavigate();
  const { cardAccount } = useOutletContext<CardAccountProps>();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [cardAccount?.id, currentPlatform?.id, isSandbox, resetPagination]);

  const { data: cardsData, isLoading } = useQuery({
    queryKey: ['card-account-cards', cardAccount?.id, currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardRepository.listCards({ cardAccountId: cardAccount.id, limit: pageSize, startingAfter: cursor }),
    enabled: !!cardAccount?.id,
    placeholderData: keepPreviousData,
  });

  const cards = cardsData?.cards ?? [];
  const hasMore = Boolean(cardsData?.hasMore);

  return (
    <Inner p={0}>
      <Section headline="Cards">
        <Loading isLoading={isLoading}>
          <Inner px="var(--page-margin)" py={0}>
            {cards.length === 0 && !hasPrevious ? (
              <EmptyState headline="No cards yet" text="Cards will appear here for this card account." />
            ) : (
              <Table<Card>
                columns={columns}
                data={cards}
                variant={TableVariant.List}
                pagination={{
                  hasNext: hasMore,
                  hasPrevious,
                  pageIndex,
                  pageSize,
                  onNext: () => {
                    if (cards.length > 0) {
                      onNext({ cursor: cards[cards.length - 1].id });
                    }
                  },
                  onPrevious,
                  onPageSizeChange: setPageSize,
                }}
                onRowClick={(row) => {
                  navigate(`${ROUTE.CARDS}/edit/${row.id}`);
                }}
              />
            )}
          </Inner>
        </Loading>
      </Section>
    </Inner>
  );
};
